import React, { useState, useEffect } from 'react';
import { Book, Search, Calendar, Tag, Trash2, Heart, MessageSquare, BookOpen, Filter, ChevronDown, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFavoritesStore } from '../stores/favoritesStore';
import { supabase, type VerseNote, saveNote, deleteNote, getAllNotes } from '../lib/supabase';
import type { BibleVerse } from '../lib/types';
import { format } from 'date-fns';

const OLD_TESTAMENT_BOOKS = [
  'Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy',
  'Joshua', 'Judges', 'Ruth', '1 Samuel', '2 Samuel',
  '1 Kings', '2 Kings', '1 Chronicles', '2 Chronicles',
  'Ezra', 'Nehemiah', 'Esther', 'Job', 'Psalms',
  'Proverbs', 'Ecclesiastes', 'Song of Solomon', 'Isaiah',
  'Jeremiah', 'Lamentations', 'Ezekiel', 'Daniel', 'Hosea',
  'Joel', 'Amos', 'Obadiah', 'Jonah', 'Micah', 'Nahum',
  'Habakkuk', 'Zephaniah', 'Haggai', 'Zechariah', 'Malachi'
];

const NEW_TESTAMENT_BOOKS = [
  'Matthew', 'Mark', 'Luke', 'John', 'Acts', 'Romans',
  '1 Corinthians', '2 Corinthians', 'Galatians', 'Ephesians',
  'Philippians', 'Colossians', '1 Thessalonians', '2 Thessalonians',
  '1 Timothy', '2 Timothy', 'Titus', 'Philemon', 'Hebrews',
  'James', '1 Peter', '2 Peter', '1 John', '2 John', '3 John',
  'Jude', 'Revelation'
];

export function NotesPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'notes' | 'favorites'>('notes');
  const [notes, setNotes] = useState<VerseNote[]>([]);
  const [favorites, setFavorites] = useState<BibleVerse[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();
  const { getFavorites, toggleFavorite } = useFavoritesStore();

  // Separate filter states for notes and favorites
  const [notesFilters, setNotesFilters] = useState({
    testament: 'all' as 'all' | 'old' | 'new',
    book: '',
    dateRange: 'all' as 'all' | 'today' | 'week' | 'month'
  });

  const [favoritesFilters, setFavoritesFilters] = useState({
    testament: 'all' as 'all' | 'old' | 'new',
    book: '',
    dateRange: 'all' as 'all' | 'today' | 'week' | 'month'
  });

  useEffect(() => {
    loadNotes();
    loadFavorites();

    // Set up an interval to refresh favorites every 2 seconds
    const intervalId = setInterval(loadFavorites, 2000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
  }, [favoritesFilters]);

  const loadNotes = async () => {
    try {
      const allNotes = await getAllNotes();
      setNotes(allNotes || []);
    } catch (error) {
      console.error('Failed to load notes:', error);
      setNotes([]);
    }
  };

  const loadFavorites = async () => {
    try {
      const allFavorites = await getFavorites();
      setFavorites(allFavorites.map(f => ({
        id: f.id,
        book: f.book,
        chapter: f.chapter,
        verse: f.verse,
        text: f.text,
        version: f.version,
        reference: `${f.book} ${f.chapter}:${f.verse}`,
        timestamp: f.timestamp
      })));
    } catch (error) {
      console.error('Failed to load favorites:', error);
    }
  };

  // Helper function to normalize book names
  const normalizeBookName = (bookName: string) => {
    // Capitalize first letter of each word
    return bookName.toLowerCase().split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const isNewTestamentBook = (bookName: string) => {
    const normalizedBook = normalizeBookName(bookName);
    return NEW_TESTAMENT_BOOKS.some(book => normalizeBookName(book) === normalizedBook);
  };

  const isOldTestamentBook = (bookName: string) => {
    const normalizedBook = normalizeBookName(bookName);
    return OLD_TESTAMENT_BOOKS.some(book => normalizeBookName(book) === normalizedBook);
  };

  const getAvailableBooks = (testament: 'all' | 'old' | 'new') => {
    switch (testament) {
      case 'old':
        return OLD_TESTAMENT_BOOKS.sort();
      case 'new':
        return NEW_TESTAMENT_BOOKS.sort();
      default:
        return [...OLD_TESTAMENT_BOOKS, ...NEW_TESTAMENT_BOOKS].sort();
    }
  };

  const isWithinDateRange = (date: string) => {
    if (notesFilters.dateRange === 'all') return true;
    
    const noteDate = new Date(date);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());

    switch (notesFilters.dateRange) {
      case 'today':
        return noteDate >= today;
      case 'week':
        return noteDate >= weekAgo;
      case 'month':
        return noteDate >= monthAgo;
      default:
        return true;
    }
  };

  // Filter notes based on search term and filters
  const filteredNotes = notes.filter(note => {
    // Skip notes with missing data
    if (!note || !note.content || !note.verse_id) {
      return false;
    }

    const matchesSearch = 
      note.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.verse_id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const noteBook = note.verse_id.split('_')[0];
    const matchesTestament = notesFilters.testament === 'all' || 
      (notesFilters.testament === 'new' ? NEW_TESTAMENT_BOOKS.includes(noteBook) : OLD_TESTAMENT_BOOKS.includes(noteBook));
    const matchesBook = !notesFilters.book || noteBook === notesFilters.book;
    const matchesDate = isWithinDateRange(note.created_at);

    return matchesSearch && matchesTestament && matchesBook && matchesDate;
  });

  // Filter favorites based on search term and filters
  const filteredFavorites = favorites.filter(verse => {
    const matchesSearch = 
      verse.reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
      verse.text.toLowerCase().includes(searchTerm.toLowerCase());

    // Extract and normalize book name from the verse reference
    const verseBook = normalizeBookName(verse.reference.split(' ')[0]);
    
    // Check testament match using normalized book names
    let matchesTestament = favoritesFilters.testament === 'all';
    if (!matchesTestament) {
      const testamentBooks = favoritesFilters.testament === 'new' ? NEW_TESTAMENT_BOOKS : OLD_TESTAMENT_BOOKS;
      matchesTestament = testamentBooks.includes(verseBook);
    }
    
    // Check book match using normalized names
    const matchesBook = !favoritesFilters.book || 
                       normalizeBookName(favoritesFilters.book) === verseBook;

    // Check date range
    const matchesDate = isWithinDateRange(verse.timestamp);

    return matchesSearch && matchesTestament && matchesBook && matchesDate;
  });

  const handleDeleteNote = async (verseId: string) => {
    try {
      await deleteNote(verseId);
      await loadNotes(); // Reload notes after deletion
    } catch (error) {
      console.error('Failed to delete note:', error);
    }
  };

  const handleSaveNote = async (verseId: string, content: string) => {
    try {
      await saveNote(verseId, content);
      await loadNotes(); // Reload notes after saving
    } catch (error) {
      console.error('Failed to save note:', error);
    }
  };

  const handleReadInContext = (verse: BibleVerse) => {
    const bookPath = verse.book.toLowerCase().replace(/\s+/g, '-');
    navigate(`/read/${verse.version}/${bookPath}/${verse.chapter}?verse=${verse.verse}`);
  };

  const handleRemoveFavorite = async (verse: BibleVerse) => {
    try {
      await toggleFavorite({
        id: verse.id,
        book: verse.book,
        chapter: verse.chapter,
        verse: verse.verse,
        text: verse.text,
        version: verse.version
      });
      // Immediately refresh favorites
      loadFavorites();
    } catch (error) {
      console.error('Failed to remove favorite:', error);
    }
  };

  const handleClearDatabase = async () => {
    if (window.confirm('Are you sure you want to clear all notes? This cannot be undone.')) {
      // await notesApi.clearDatabase();
      await loadNotes();
    }
  };

  const clearFilters = () => {
    if (activeTab === 'notes') {
      setNotesFilters({
        testament: 'all',
        book: '',
        dateRange: 'all'
      });
    } else {
      setFavoritesFilters({
        testament: 'all',
        book: '',
        dateRange: 'all'
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="scripture-card">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Book className="text-gold-400" size={24} />
            <h2 className="text-xl font-semibold text-white">Study Notes</h2>
          </div>
          <div className="flex items-center gap-4 flex-1 max-w-2xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={20} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search notes and favorites..."
                className="w-full pl-10 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-gold-400 transition-colors"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFilters ? 'bg-gold-400 text-gray-900' : 'bg-white/5 text-white/60 hover:text-white'
              }`}
            >
              <Filter size={18} />
              <span>Filters</span>
              <ChevronDown size={16} className={`transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white/80 font-medium">Filters</h3>
              <button
                onClick={clearFilters}
                className="text-white/40 hover:text-white/60 text-sm flex items-center gap-1"
              >
                <X size={14} />
                Clear all
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Notes Filters */}
              {activeTab === 'notes' && (
                <>
                  <div>
                    <label className="block text-white/60 text-sm mb-2">Testament</label>
                    <select
                      value={notesFilters.testament}
                      onChange={(e) => {
                        setNotesFilters({
                          ...notesFilters,
                          testament: e.target.value as 'all' | 'old' | 'new',
                          book: '' // Reset book when testament changes
                        });
                      }}
                      className="w-full bg-obsidian-800 border border-white/10 rounded-lg px-3 py-2 text-white"
                      style={{ color: 'white' }}
                    >
                      <option value="all" className="bg-obsidian-900 text-white">All</option>
                      <option value="old" className="bg-obsidian-900 text-white">Old Testament</option>
                      <option value="new" className="bg-obsidian-900 text-white">New Testament</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-2">Book</label>
                    <select
                      value={notesFilters.book}
                      onChange={(e) => {
                        setNotesFilters({
                          ...notesFilters,
                          book: e.target.value
                        });
                      }}
                      className="w-full bg-obsidian-800 border border-white/10 rounded-lg px-3 py-2 text-white"
                      style={{ color: 'white' }}
                    >
                      <option value="" className="bg-obsidian-900 text-white">All Books</option>
                      {getAvailableBooks(notesFilters.testament).map(book => (
                        <option key={book} value={book} className="bg-obsidian-900 text-white">{book}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-2">Date Range</label>
                    <select
                      value={notesFilters.dateRange}
                      onChange={(e) => setNotesFilters({
                        ...notesFilters,
                        dateRange: e.target.value as 'all' | 'today' | 'week' | 'month'
                      })}
                      className="w-full bg-obsidian-800 border border-white/10 rounded-lg px-3 py-2 text-white"
                      style={{ color: 'white' }}
                    >
                      <option value="all" className="bg-obsidian-900 text-white">All Time</option>
                      <option value="today" className="bg-obsidian-900 text-white">Today</option>
                      <option value="week" className="bg-obsidian-900 text-white">Past Week</option>
                      <option value="month" className="bg-obsidian-900 text-white">Past Month</option>
                    </select>
                  </div>
                </>
              )}
              
              {/* Favorites Filters */}
              {activeTab === 'favorites' && (
                <>
                  <div>
                    <label className="block text-white/60 text-sm mb-2">Testament</label>
                    <select
                      value={favoritesFilters.testament}
                      onChange={(e) => {
                        setFavoritesFilters({
                          ...favoritesFilters,
                          testament: e.target.value as 'all' | 'old' | 'new',
                          book: '' // Reset book when testament changes
                        });
                      }}
                      className="w-full bg-obsidian-800 border border-white/10 rounded-lg px-3 py-2 text-white"
                      style={{ color: 'white' }}
                    >
                      <option value="all" className="bg-obsidian-900 text-white">All</option>
                      <option value="old" className="bg-obsidian-900 text-white">Old Testament</option>
                      <option value="new" className="bg-obsidian-900 text-white">New Testament</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-2">Book</label>
                    <select
                      value={favoritesFilters.book}
                      onChange={(e) => {
                        setFavoritesFilters({
                          ...favoritesFilters,
                          book: e.target.value
                        });
                      }}
                      className="w-full bg-obsidian-800 border border-white/10 rounded-lg px-3 py-2 text-white"
                      style={{ color: 'white' }}
                    >
                      <option value="" className="bg-obsidian-900 text-white">All Books</option>
                      {getAvailableBooks(favoritesFilters.testament).map(book => (
                        <option key={book} value={book} className="bg-obsidian-900 text-white">{book}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-2">Date Range</label>
                    <select
                      value={favoritesFilters.dateRange}
                      onChange={(e) => setFavoritesFilters({
                        ...favoritesFilters,
                        dateRange: e.target.value as 'all' | 'today' | 'week' | 'month'
                      })}
                      className="w-full bg-obsidian-800 border border-white/10 rounded-lg px-3 py-2 text-white"
                      style={{ color: 'white' }}
                    >
                      <option value="all" className="bg-obsidian-900 text-white">All Time</option>
                      <option value="today" className="bg-obsidian-900 text-white">Today</option>
                      <option value="week" className="bg-obsidian-900 text-white">Past Week</option>
                      <option value="month" className="bg-obsidian-900 text-white">Past Month</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('notes')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'notes'
                ? 'bg-gold-400 text-gray-900'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <MessageSquare size={18} />
            Notes ({notes.length})
          </button>
          <button
            onClick={() => setActiveTab('favorites')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'favorites'
                ? 'bg-gold-400 text-gray-900'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <Heart size={18} />
            Favorites ({favorites.length})
          </button>
        </div>

        <div className="space-y-4">
          {activeTab === 'notes' ? (
            filteredNotes.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="mx-auto mb-4 text-white/20" size={48} />
                <p className="text-white/60">No notes found. Start adding notes while reading!</p>
              </div>
            ) : (
              filteredNotes.map((note) => (
                <div key={note.id} className="p-4 bg-white/5 rounded-lg border border-white/10">
                  <div className="flex justify-between items-start gap-4 mb-2">
                    <h3 className="text-gold-400 font-medium flex items-center gap-2">
                      {(() => {
                        const [book, chapter, verse] = note.verse_id.split('_');
                        return `${book.charAt(0).toUpperCase() + book.slice(1)} ${chapter}:${verse}`;
                      })()}
                    </h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          const [book, chapter, verse] = note.verse_id.split('_');
                          const formattedBook = book.charAt(0).toUpperCase() + book.slice(1);
                          handleReadInContext({
                            id: note.verse_id,
                            book: formattedBook,
                            chapter: parseInt(chapter),
                            verse: parseInt(verse),
                            version: 'kjv',
                            text: '',
                            reference: `${formattedBook} ${chapter}:${verse}`
                          });
                        }}
                        className="p-1 text-white/40 hover:text-gold-400 transition-colors"
                        title="Read in context"
                      >
                        <BookOpen size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note.verse_id)}
                        className="p-1 hover:text-red-400 transition-colors"
                        title="Delete note"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <p className="text-white/80 whitespace-pre-wrap">{note.content}</p>
                </div>
              ))
            )
          ) : (
            filteredFavorites.length === 0 ? (
              <div className="text-center py-12">
                <Heart className="mx-auto mb-4 text-white/20" size={48} />
                <p className="text-white/60">No favorites found. Add verses to your favorites while reading!</p>
              </div>
            ) : (
              filteredFavorites.map((verse) => (
                <div key={verse.id} className="p-4 bg-white/5 rounded-lg border border-white/10">
                  <div className="flex justify-between items-start gap-4 mb-2">
                    <h3 className="text-gold-400 font-medium flex items-center gap-2">
                      {verse.reference}
                      <span className="text-sm text-white/40">({verse.version.toUpperCase()})</span>
                    </h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleReadInContext(verse)}
                        className="p-1 text-white/40 hover:text-gold-400 transition-colors"
                        title="Read in context"
                      >
                        <BookOpen size={16} />
                      </button>
                      <button
                        onClick={() => handleRemoveFavorite(verse)}
                        className="p-1 text-red-400 transition-colors hover:text-red-300"
                        title="Remove from favorites"
                      >
                        <Heart size={16} className="fill-current" />
                      </button>
                    </div>
                  </div>
                  <p className="text-white/80 font-serif text-lg leading-relaxed mb-4">
                    {verse.text}
                  </p>
                </div>
              ))
            )
          )}
        </div>
      </div>
    </div>
  );
}